<script lang="ts">
	import { create } from "@bufbuild/protobuf";
	import { Img } from "flowbite-svelte";
	import { ImagePlaceholder } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { ConversionType } from "#gen/bundles/v1/bundle_pb.js";
	import { ImageSchema, type Image } from "#gen/bundles/v1/common_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import { HORIZONTAL_ALIGNMENTS, IMAGE_BORDER_RADIUS, IMAGE_SIZES, type BorderRadius } from "#lib/components/flow/editor/appearance/constants.js";
	import ImageCarousel from "#lib/components/flow/viewer/ImageCarousel.svelte";
	import { DEFAULT_IMAGE_ASPECT_RATIO, LANDSCAPE_IMAGE_ASPECT_RATIO } from "#lib/components/images/constants.js";
	import type { ComponentProps } from "#lib/components/page/ComponentProps.js";
	import { isTypeUriVersionAtLeast } from "#lib/components/page/utils.js";
	import { isFeatureEnabled } from "#lib/featureFlags.js";
	import type { JwtPayload } from "#lib/jwt.js";
	import { booleanValue, numberValue, stringValue, structValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.ts";
	import type { Client } from "@optimizely/optimizely-sdk";
	import type { Writable } from "svelte/store";

	const provider: Writable<TokenProvider> = getContext("tokenProvider");
	const flags: Client = getContext("flags");
	const claims: Writable<JwtPayload> = getContext("claims");

	let { id, style, page, mode, typeUri, connectable = false, connectionRequired = false }: ComponentProps = $props();

	let invalid = $state(false);

	const pageEditingEnabled = $derived(isFeatureEnabled(flags, "build_your_own_page", $claims));
	const config = $derived(structValue($page.config, id));
	const imagesKey = $derived(config?.imagesKey as string | undefined);
	const alt = $derived(stringValue(config, "alt"));
	const className = $derived(stringValue(config, "class"));
	const imageSize = $derived(stringValue(config, "imageSize") ?? getSizeStylesFromClass(className) ?? IMAGE_SIZES[0].value);
	const borderRadius = $derived((stringValue(config, "borderRadius") as BorderRadius) ?? IMAGE_BORDER_RADIUS[0].value);
	const imageAlignment = $derived(stringValue(config, "imageAlignment") ?? HORIZONTAL_ALIGNMENTS[0].value);
	const isPlaceholder = $derived(mode === Mode.EDITING && !config?.src && !imagesKey && !(Array.isArray(config?.images) && config.images.length > 0));
	const aspectRatio = $derived.by(() => {
		const configured = numberValue(config, "aspectRatio");
		if (configured !== undefined) return configured;
		if (isPlaceholder && isTypeUriVersionAtLeast(typeUri, "2.0.0")) return LANDSCAPE_IMAGE_ASPECT_RATIO;
		if (mode === Mode.EDITING) return DEFAULT_IMAGE_ASPECT_RATIO;
		return undefined;
	});
	const hidden = $derived(booleanValue(config, "hidden"));
	const disabled = $derived(booleanValue(config, "disabled"));

	const execute: (conversionType: ConversionType, conversionComponent: string, sourceAnchor?: string) => Promise<boolean> = getContext("execute");
	const isComponentConnected: ((componentId: string) => boolean) | undefined = getContext("isComponentConnected");

	// An Img acts as a tappable link only when it actually has an outgoing connection.
	// Resolved declaratively so it is correct on first (server) render — no effect delay.
	const isConnected = $derived(isComponentConnected?.(id) ?? false);
	const interactive = $derived(mode !== Mode.EDITING && isConnected && !disabled);

	function resolveImages(imagesKey: string | undefined): Image[] {
		if (!imagesKey) return (config?.images as unknown as Image[]) ?? [];
		const json = JSON.stringify({ images: imagesKey });

		return JSON.parse(json).images ?? [];
	}

	function getSizeStylesFromClass(className: string | undefined): string | undefined {
		if (!className) return undefined;
		for (const [key, value] of Object.entries(sizeStylesFromClass)) {
			if (className.includes(value)) {
				return key;
			}
		}
		return undefined;
	}

	const images = $derived.by(() => {
		const list = resolveImages(imagesKey);
		if (list.length > 0) return list;
		if (pageEditingEnabled && mode === Mode.EDITING && !config?.src) return [create(ImageSchema, { url: "" })];
		return [];
	});

	const sizeStyles: Record<string, string> = {
		xs: "width: 96px; max-width: 100%;",
		sm: "width: 140px; max-width: 100%;",
		md: "width: 200px; max-width: 100%;",
		lg: "width: 100%; max-width: 600px;",
	};
	const sizeStylesFromClass: Record<string, string> = {
		xs: "w-24",
		sm: "w-35",
		md: "w-50",
		lg: "w-full",
	};
	const radiusStyles: Record<string, string> = {
		sm: "border-radius: 6px;",
		md: "border-radius: 12px;",
		lg: "border-radius: 24px;",
		full: "border-radius: 100%;",
		none: "border-radius: 0;",
	};

	const imgAppearanceStyle = $derived.by(() => {
		if (images.length < 1) {
			return "";
		}
		return [imageSize && sizeStyles[imageSize], aspectRatio !== undefined ? `aspect-ratio: ${aspectRatio};` : undefined, borderRadius && radiusStyles[borderRadius]].filter(Boolean).join("");
	});

	const containerStyle = $derived.by(() => {
		if (images.length < 1) {
			return "";
		}
		return [imageAlignment && `justify-content: ${imageAlignment}; width: 100%; display: flex; align-items: flex-start;`].filter(Boolean).join("");
	});

	async function handleClick(e: MouseEvent) {
		e.preventDefault();
		e.stopPropagation();
		if (!disabled) {
			await execute(ConversionType.NONE, id, id);
		}
	}

	async function handleKeyDown(e: KeyboardEvent) {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();
			if (!disabled) {
				await execute(ConversionType.NONE, id, id);
			}
		}
	}
</script>

{#snippet placeholder()}
	<ImagePlaceholder size="sm" imgOnly={true} class={twMerge("h-full w-full animate-none", className)} classes={{ image: "h-full sm:w-full w-full bg-gray-50 m-0", svg: "h-6 w-6 text-gray-400" }} rounded={borderRadius ?? "none"} />
{/snippet}

{#snippet imageContent()}
	{#if images.length > 0}
		<div style={containerStyle} data-linkable-id={connectable ? id : undefined} data-connection-required={connectable ? String(connectionRequired) : undefined}>
			{#if interactive}
				<div class="relative cursor-pointer overflow-hidden" style={imgAppearanceStyle} role="button" tabindex="0" aria-label={alt || "Image"} onclick={mode === Mode.EDITING ? undefined : handleClick} onkeydown={handleKeyDown}>
					<ImageCarousel hideGradient {images} centerDots={borderRadius === "full"} {imageSize} fill={aspectRatio !== undefined} />
				</div>
			{:else}
				<div class="relative overflow-hidden" style={imgAppearanceStyle}>
					<ImageCarousel hideGradient {images} centerDots={borderRadius === "full"} {imageSize} fill={aspectRatio !== undefined} />
				</div>
			{/if}
		</div>
	{:else if config?.src}
		{#if invalid || $provider?.hasSubstitutionToken(stringValue(config, "src"))}
			{@render placeholder()}
		{:else if interactive}
			<div class="w-fit cursor-pointer" role="button" tabindex="0" aria-label={alt || "Image"} onclick={mode === Mode.EDITING ? undefined : handleClick} onkeydown={handleKeyDown} data-linkable-id={connectable ? id : undefined} data-connection-required={connectable ? String(connectionRequired) : undefined}>
				<Img {alt} {...config} {style} onerror={() => (invalid = true)} />
			</div>
		{:else}
			<Img {alt} {...config} {style} onerror={() => (invalid = true)} data-linkable-id={connectable ? id : undefined} data-connection-required={connectable ? String(connectionRequired) : undefined} />
		{/if}
	{/if}
{/snippet}

<div class="relative w-full" class:hidden={mode !== Mode.EDITING && hidden}>
	{#if mode === Mode.EDITING && hidden}
		<div class="absolute -top-1 -right-1 z-99 flex h-7 w-7 items-center justify-center rounded-full border bg-white/90 text-gray-900">
			<Icon iconSet="heroicons" name="eye-slash" class=" h-4 w-4" />
		</div>
	{/if}
	{@render imageContent()}
</div>
